import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Upload, ArrowLeft, AlertCircle, CheckCircle, Coins, Pencil, TreePine, Camera, FileImage } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const TREE_COST = 30;

const STEPS = [
  { icon: Pencil, title: "Find paper & pen", desc: "Find a blank piece of paper and a pen, pencil, or any drawing tool" },
  { icon: TreePine, title: "Draw a tree", desc: "Draw a tree — any tree you imagine. There are no right or wrong trees. Draw naturally." },
  { icon: Camera, title: "Photograph it", desc: "Take a clear photograph of your drawing or scan it" },
  { icon: Upload, title: "Upload below", desc: "Upload your drawing below and complete the short form" },
];

export default function TreeAnalysisPage() {
  const { user, loading, refreshUser } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({
    full_name: user?.name || "",
    email: user?.email || "",
    age_range: "",
    current_role: user?.job_title || "",
    company: user?.company || "",
    notes: "",
    confirm: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");

  if (loading) {
    return <div className="min-h-screen bg-cream flex items-center justify-center pt-16"><div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" /></div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-cream pt-32 pb-20 px-4">
        <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-lg p-10 text-center">
          <AlertCircle className="w-12 h-12 text-gold mx-auto mb-4" />
          <h1 className="font-playfair text-navy text-2xl font-bold mb-3">Login Required</h1>
          <p className="font-dm text-navy/60 text-sm mb-6">Please log in to submit your Tree Analysis.</p>
          <Link to="/login" className="inline-block bg-gold text-navy font-dm font-bold px-8 py-3 rounded-lg hover:bg-gold/90 transition-colors">Log In</Link>
        </div>
      </div>
    );
  }

  const balance = user.assessment_tokens ?? 0;
  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      setError("File too large (max 10MB)");
      return;
    }
    setError("");
    setFile(f);
    if (f.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (evt) => setPreview(evt.target.result);
      reader.readAsDataURL(f);
    } else {
      setPreview(null);
    }
  };

  const handleSubmit = async () => {
    setError("");
    if (!file) { setError("Please upload your tree drawing"); return; }
    if (!form.full_name || !form.email) { setError("Name and email are required"); return; }
    if (!form.confirm) { setError("Please confirm this is your own drawing"); return; }
    if (balance < TREE_COST) { setError("Insufficient assessment tokens"); return; }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = new URLSearchParams({
        full_name: form.full_name,
        email: form.email,
        age_range: form.age_range,
        current_role: form.current_role,
        company: form.company,
        notes: form.notes,
      });
      const res = await axios.post(`${API}/api/assessments/tree/submit?${params}`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSuccess(res.data);
      await refreshUser();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "INSUFFICIENT_ASSESSMENT_TOKENS") {
        setError("You don't have enough assessment tokens to submit.");
      } else {
        setError(detail || "Failed to submit. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-cream pt-32 pb-20 px-4">
        <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-lg p-10 text-center" data-testid="tree-success-card">
          <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-4" />
          <h1 className="font-playfair text-navy text-3xl font-bold mb-3">Thank You, {form.full_name.split(" ")[0]}!</h1>
          <p className="font-dm text-navy/70 text-base mb-2 leading-relaxed">
            Your tree drawing has been received successfully. A certified RoleIQ practitioner will review your drawing and deliver your personalised report within <strong>48 hours</strong>.
          </p>
          <p className="font-dm text-navy/60 text-sm mb-6">You will receive it at <strong>{form.email}</strong> and it will appear in your dashboard under <strong>My Assessments</strong>.</p>
          <div className="bg-cream rounded-lg p-4 mb-6 inline-block">
            <p className="font-dm text-navy/70 text-sm">Tokens used: <strong className="text-gold">{TREE_COST}</strong> · Remaining: <strong className="text-gold">{success.remaining_tokens}</strong></p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/assessments" className="bg-navy text-gold font-dm font-bold px-6 py-3 rounded-lg hover:bg-navy/90 transition-colors">Return to Assessments</Link>
            <Link to="/dashboard" className="border border-navy text-navy font-dm font-bold px-6 py-3 rounded-lg hover:bg-navy/5 transition-colors">Go to My Dashboard</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream pt-28 pb-20 px-4">
      <div className="max-w-3xl mx-auto">
        <Link to="/assessments" className="inline-flex items-center gap-2 text-navy/60 hover:text-gold font-dm text-sm mb-6 transition-colors" data-testid="back-to-assessments">
          <ArrowLeft className="w-4 h-4" /> Back to Assessments
        </Link>

        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="bg-navy p-8">
            <h1 className="font-playfair text-white text-3xl font-bold mb-2">Tree Analysis — Upload Your Drawing</h1>
            <p className="font-dm text-gold/80 text-sm">A certified practitioner will deliver your personalised report within 48 hours.</p>
          </div>

          <div className="p-8 space-y-8">
            {/* Steps */}
            <div>
              <h2 className="font-playfair text-navy text-xl font-bold mb-5">How it works</h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {STEPS.map((s, i) => (
                  <div key={i} className="text-center">
                    <div className="w-14 h-14 bg-gold/10 border-2 border-gold rounded-full flex items-center justify-center mx-auto mb-2">
                      <s.icon className="w-6 h-6 text-gold" />
                    </div>
                    <p className="font-dm text-navy text-xs font-bold mb-1">Step {i + 1}</p>
                    <p className="font-dm text-navy/60 text-xs leading-snug">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Important note */}
            <div className="bg-gold/5 border-l-4 border-gold rounded-r-lg p-4">
              <p className="font-dm text-navy text-sm">
                <strong>Important:</strong> Your drawing will be reviewed by a certified RoleIQ practitioner. Your personalised analysis report will be delivered within <strong>48 hours</strong> to your email and saved in your dashboard.
              </p>
            </div>

            {/* Upload area */}
            <div>
              <label className="font-dm text-navy text-sm font-bold block mb-2">Your tree drawing</label>
              <label className="block border-2 border-dashed border-gold rounded-xl p-8 text-center cursor-pointer hover:bg-gold/5 transition-colors" data-testid="tree-upload-area">
                <input type="file" accept="image/*,application/pdf" className="hidden" onChange={handleFile} data-testid="tree-file-input" />
                {preview ? (
                  <div className="space-y-3">
                    <img src={preview} alt="Preview" className="max-h-64 mx-auto rounded-lg" />
                    <p className="font-dm text-navy text-sm font-semibold">{file?.name}</p>
                    <button type="button" onClick={(e) => { e.preventDefault(); setFile(null); setPreview(null); }}
                      className="font-dm text-red-500 text-xs hover:underline">Remove</button>
                  </div>
                ) : file ? (
                  <div>
                    <FileImage className="w-10 h-10 text-gold mx-auto mb-2" />
                    <p className="font-dm text-navy text-sm font-semibold">{file.name}</p>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-10 h-10 text-gold mx-auto mb-3" />
                    <p className="font-dm text-navy font-semibold text-sm mb-1">Click to upload or drag and drop</p>
                    <p className="font-dm text-navy/50 text-xs">JPG, PNG, PDF, HEIC · Max 10MB</p>
                  </div>
                )}
              </label>
            </div>

            {/* Form fields */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="font-dm text-navy text-xs font-bold block mb-1">Full Name *</label>
                <input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  className="w-full border border-navy/20 rounded-lg px-3 py-2.5 font-dm text-sm focus:border-gold focus:outline-none" data-testid="tree-full-name" />
              </div>
              <div>
                <label className="font-dm text-navy text-xs font-bold block mb-1">Email *</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full border border-navy/20 rounded-lg px-3 py-2.5 font-dm text-sm focus:border-gold focus:outline-none" data-testid="tree-email" />
              </div>
              <div>
                <label className="font-dm text-navy text-xs font-bold block mb-1">Age Range</label>
                <select value={form.age_range} onChange={(e) => setForm({ ...form, age_range: e.target.value })}
                  className="w-full border border-navy/20 rounded-lg px-3 py-2.5 font-dm text-sm focus:border-gold focus:outline-none" data-testid="tree-age-range">
                  <option value="">Select…</option>
                  <option>Under 25</option>
                  <option>25-35</option>
                  <option>35-45</option>
                  <option>45-55</option>
                  <option>55+</option>
                </select>
              </div>
              <div>
                <label className="font-dm text-navy text-xs font-bold block mb-1">Current Role</label>
                <input value={form.current_role} onChange={(e) => setForm({ ...form, current_role: e.target.value })}
                  className="w-full border border-navy/20 rounded-lg px-3 py-2.5 font-dm text-sm focus:border-gold focus:outline-none" data-testid="tree-role" />
              </div>
              <div className="sm:col-span-2">
                <label className="font-dm text-navy text-xs font-bold block mb-1">Company</label>
                <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="w-full border border-navy/20 rounded-lg px-3 py-2.5 font-dm text-sm focus:border-gold focus:outline-none" data-testid="tree-company" />
              </div>
              <div className="sm:col-span-2">
                <label className="font-dm text-navy text-xs font-bold block mb-1">What are you hoping to understand from this analysis? <span className="font-normal text-navy/40">(optional)</span></label>
                <textarea value={form.notes} maxLength={1500} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full border border-navy/20 rounded-lg px-3 py-2.5 font-dm text-sm focus:border-gold focus:outline-none min-h-[80px]" data-testid="tree-notes" />
              </div>
              <div className="sm:col-span-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.checked })} className="mt-1" data-testid="tree-confirm" />
                  <span className="font-dm text-navy/70 text-xs">I confirm this is my own drawing and I'm submitting it for analysis.</span>
                </label>
              </div>
            </div>

            {/* Token info */}
            <div className="bg-cream border border-gold/30 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Coins className="w-5 h-5 text-gold" />
                <p className="font-dm text-navy text-sm">This submission uses <strong className="text-gold">{TREE_COST} assessment tokens</strong></p>
              </div>
              <p className="font-dm text-navy/60 text-xs">You have: <strong className="text-gold">{balance} tokens</strong></p>
            </div>

            {balance < TREE_COST && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="font-dm text-red-700 text-sm mb-2">You don't have enough assessment tokens.</p>
                <Link to="/pricing#assessment-packs" className="inline-block bg-navy text-gold font-dm font-bold px-5 py-2 rounded-lg text-sm hover:bg-navy/90 transition-colors">Buy Assessment Tokens</Link>
              </div>
            )}

            {error && <p className="font-dm text-red-600 text-sm">{error}</p>}

            <button onClick={handleSubmit} disabled={submitting || balance < TREE_COST} data-testid="tree-submit-btn"
              className="w-full bg-navy text-gold font-dm font-bold py-4 rounded-lg text-base hover:bg-navy/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {submitting ? "Submitting…" : "Submit for Analysis"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
